import { z } from "zod";

/**
 * Physical events and venue check-in.
 *
 * An event belongs to a form and is entirely optional: a form with no events has no check-in and
 * nothing extra to configure. The guest list is uploaded rather than derived from who registered,
 * because speakers, officials and programme staff attend without ever filling the form in.
 */

/**
 * An ISO 8601 instant, parsed to a Date here so no route handler has to.
 *
 * `z.coerce.date()` would accept "not a date" as an Invalid Date and hand it to Prisma; this
 * refuses it at the edge, which is where CLAUDE.md's rule against date arithmetic on strings starts
 * being enforceable.
 */
const instant = z
  .string()
  .datetime({ offset: true })
  .transform((value) => new Date(value));

export const eventCreateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    venue: z.string().trim().max(300).nullable().optional(),
    startsAt: instant,
    endsAt: instant.nullable().optional(),
    checkInOpensAt: instant.nullable().optional(),
    checkInClosesAt: instant.nullable().optional(),
    /** Staff markup, not HTML — the same subset the privacy notice uses. */
    invitationMessage: z.string().max(20_000).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    // An event that ends before it starts is a typo every time, and it silently produces an
    // event that never appears in a "happening now" list.
    if (value.endsAt && value.endsAt <= value.startsAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endsAt"],
        message: "The event cannot end before it starts.",
      });
    }
    if (
      value.checkInOpensAt &&
      value.checkInClosesAt &&
      value.checkInClosesAt <= value.checkInOpensAt
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["checkInClosesAt"],
        message: "Check-in cannot close before it opens.",
      });
    }
  });

export const eventUpdateRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  venue: z.string().trim().max(300).nullable().optional(),
  startsAt: instant.optional(),
  endsAt: instant.nullable().optional(),
  checkInOpensAt: instant.nullable().optional(),
  checkInClosesAt: instant.nullable().optional(),
  invitationMessage: z.string().max(20_000).nullable().optional(),
  isActive: z.boolean().optional(),
});

/**
 * One row of an uploaded guest list.
 *
 * Deliberately just a name and an address. Anything more is data the platform would then be
 * holding about someone who never filled a form and never consented to anything — and the venue
 * needs to know who is at the door, not their date of birth.
 */
export const eventInviteRowSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
});

/**
 * The per-event staff roles, in the order they appear in a picker — widest first.
 *
 * `MANAGER` grants everything on that one event; `DOOR` grants the entrance and nothing else, so
 * someone drafted in for an afternoon can scan codes without also gaining every applicant's
 * submission. Both are additive to whatever programme role the person already holds.
 */
export const EVENT_STAFF_ROLES = ["MANAGER", "DOOR"] as const;
export type EventStaffRole = (typeof EVENT_STAFF_ROLES)[number];

export const eventStaffAssignRequestSchema = z.object({
  userId: z.string().min(1),
  role: z.enum(EVENT_STAFF_ROLES),
});

/**
 * Searching and paging the guest list.
 *
 * Offset rather than the keyset cursor the submissions table uses, deliberately: a guest list is
 * bounded (the import caps at 5,000), it has one stable sort, and the screen it feeds shows
 * "page 3 of 12" — which a cursor cannot answer. The submissions table has none of those three
 * properties, which is why it pays for keyset.
 */
export const eventInviteListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  /** Which slice of the list: everyone, or one stage of the journey through it. */
  status: z.enum(["all", "not_invited", "invited", "arrived", "not_arrived"]).default("all"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const eventInviteImportRequestSchema = z
  .object({
    /**
     * Parsed rows. The dashboard can post these directly, post a CSV to the same endpoint as
     * multipart, or paste free text as `text` — every route ends in the same validated rows.
     */
    rows: z.array(eventInviteRowSchema).max(5_000).optional(),
    /**
     * Names and addresses typed or pasted straight into the screen, one guest per line.
     *
     * Parsed on the server rather than in the browser so there is one implementation of "what
     * counts as a guest line", testable where the rest of the import is.
     */
    text: z.string().max(500_000).optional(),
    /** False (the default) reconciles and reports without writing anything. */
    apply: z.boolean().default(false),
  })
  .refine((value) => Boolean(value.rows?.length) || Boolean(value.text?.trim()), {
    message: "Provide rows, text, or a CSV file.",
    path: ["rows"],
  });

export const eventInviteSendRequestSchema = z.object({
  /** Specific invites, or every invite that has not been emailed yet when omitted. */
  inviteIds: z.array(z.string().min(1)).max(5_000).optional(),
  /** Re-send to people who already received one. Off by default: nobody wants a duplicate. */
  resend: z.boolean().default(false),
});

/**
 * What the scanner posts. `code` is whatever the camera decoded — the ticket URL the QR actually
 * encodes, or a bare token when someone pastes one. The server extracts the token from either, so
 * the scanner never has to know the URL shape.
 */
export const eventCheckInRequestSchema = z.object({
  code: z.string().trim().min(1).max(2_000),
  method: z.enum(["qr", "manual"]).default("qr"),
});

export type EventCreateRequest = z.infer<typeof eventCreateRequestSchema>;
export type EventUpdateRequest = z.infer<typeof eventUpdateRequestSchema>;
export type EventInviteRow = z.infer<typeof eventInviteRowSchema>;
export type EventInviteImportRequest = z.infer<typeof eventInviteImportRequestSchema>;
export type EventInviteSendRequest = z.infer<typeof eventInviteSendRequestSchema>;
export type EventCheckInRequest = z.infer<typeof eventCheckInRequestSchema>;
export type EventStaffAssignRequest = z.infer<typeof eventStaffAssignRequestSchema>;
export type EventInviteListQuery = z.infer<typeof eventInviteListQuerySchema>;
